import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, ProgressBar, Row } from 'react-bootstrap';
import { submitWaitlistApplication } from 'helpers/waitlist';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'components/PhoneInput';
import { useSearchParams } from 'react-router-dom';
import {
  BUSINESS_TYPE_OPTIONS,
  DAILY_INQUIRY_OPTIONS,
  DISCOVERY_OPTIONS,
  EMPLOYEE_RANGE_OPTIONS,
  getOptionLabel,
  GOAL_INTENT_OPTIONS,
  MONTHLY_SALES_OPTIONS,
  parseBooleanSelect,
  PREFERRED_LANGUAGE_OPTIONS,
  PRIMARY_CATEGORY_OPTIONS,
  PRIMARY_PROBLEM_OPTIONS,
  SALES_UPLIFT_OPTIONS,
  WILLINGNESS_TO_PAY_OPTIONS,
  YES_NO_OPTIONS
} from '../waitlistOptions';

const STEPS = ['basic', 'business', 'operation', 'fit', 'confirm'];

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
  phone: '',
  country: '',
  city: '',
  preferred_language: '',
  business_type: '',
  business_document: '',
  employee_range: '',
  ml_monthly_sales_range: '',
  has_active_ml_account: '',
  is_registered_brand: '',
  has_official_store: '',
  uses_mercado_envios_full: '',
  primary_category: '',
  primary_problem: '',
  responds_manually: '',
  uses_automation_tool: '',
  automation_tool_name: '',
  daily_inquiry_volume: '',
  goal_intent: '',
  willingness_to_pay: '',
  discovery_source: '',
  sales_uplift_expectation: '',
  sample_customer_question: '',
  referral_name: '',
  campaign_code: '',
  referral_benefit_choice: '',
  partner_ref_token: '',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  discovery_notes: '',
  sales_link_token: '',
};

const WaitlistForm = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const partnerRefToken = String(searchParams.get('ref') || '').trim();
    const salesLinkToken = String(searchParams.get('sales_link') || '').trim();
    if (salesLinkToken) {
      setFormData(currentState => ({
        ...currentState,
        sales_link_token: salesLinkToken
      }));
    }
    if (!partnerRefToken) {
      return;
    }

    setFormData(currentState => ({
      ...currentState,
      partner_ref_token: partnerRefToken,
    }));
  }, [searchParams]);

  const getDiscoveryLabel = value => {
    return getOptionLabel(DISCOVERY_OPTIONS, value, t);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'preferred_language' && value) {
      localStorage.setItem('i18nextLng', value);
      i18n.changeLanguage(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const canAdvance = () => {
    if (step === 0) {
      return formData.first_name.trim()
        && formData.last_name.trim()
        && formData.email.trim()
        && formData.password.length >= 8
        && formData.password === formData.confirm_password
        && formData.phone.trim()
        && formData.country.trim()
        && formData.city.trim()
        && formData.preferred_language;
    }
    return true;
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await submitWaitlistApplication({
        ...formData,
        has_active_ml_account: parseBooleanSelect(formData.has_active_ml_account),
        is_registered_brand: parseBooleanSelect(formData.is_registered_brand),
        has_official_store: parseBooleanSelect(formData.has_official_store),
        uses_mercado_envios_full: parseBooleanSelect(formData.uses_mercado_envios_full),
        responds_manually: parseBooleanSelect(formData.responds_manually),
        uses_automation_tool: parseBooleanSelect(formData.uses_automation_tool),
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.error || t('WAITLIST.ERROR_GENERIC');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="p-4">
        <Card.Body className="text-center">
          <h4 className="text-success mb-3">{t('WAITLIST.SUBMITTED_TITLE')}</h4>
          <p>{t('WAITLIST.SUBMITTED_MSG', { name: formData.first_name })}</p>
          <p>{t('WAITLIST.SUBMITTED_CONTACT', { email: formData.email })}</p>
        </Card.Body>
      </Card>
    );
  }

  const progress = Math.round(((step + 1) / STEPS.length) * 100);
  const isOptionalStep = step > 0 && step < STEPS.length - 1;

  return (
    <Form onSubmit={handleSubmit}>
      <ProgressBar now={progress} label={`${progress}%`} className="mb-4" />

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* Step 0: Basic Info */}
      {step === 0 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.BASIC')}</h5>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.FIRST_NAME')} *</Form.Label>
              <Form.Control name="first_name" value={formData.first_name} onChange={handleChange} required />
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.LAST_NAME')} *</Form.Label>
              <Form.Control name="last_name" value={formData.last_name} onChange={handleChange} required />
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.EMAIL')} *</Form.Label>
            <Form.Control name="email" type="email" value={formData.email} onChange={handleChange} required />
          </Form.Group>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.PASSWORD')} *</Form.Label>
              <Form.Control name="password" type="password" value={formData.password} onChange={handleChange} required minLength={8} isInvalid={formData.password.length > 0 && formData.password.length < 8} />
              <Form.Text className="text-muted">{t('WAITLIST.FIELDS.PASSWORD_HELP')}</Form.Text>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.CONFIRM_PASSWORD')} *</Form.Label>
              <Form.Control name="confirm_password" type="password" value={formData.confirm_password} onChange={handleChange} required isInvalid={formData.confirm_password.length > 0 && formData.password !== formData.confirm_password} />
              {formData.confirm_password.length > 0 && formData.password !== formData.confirm_password && (
                <Form.Control.Feedback type="invalid">{t('WAITLIST.FIELDS.PASSWORD_MISMATCH')}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <PhoneInput
              label={t('WAITLIST.FIELDS.WHATSAPP')}
              value={formData.phone}
              onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
              country={formData.country}
              onCountryChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
              placeholder={t('WAITLIST.FIELDS.WHATSAPP_PLACEHOLDER')}
              required={true}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.STATE')} *</Form.Label>
            <Form.Control name="city" value={formData.city} onChange={handleChange} required />
          </Form.Group>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.PREFERRED_LANGUAGE')} *</Form.Label>
              <Form.Select name="preferred_language" value={formData.preferred_language} onChange={handleChange} required>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {PREFERRED_LANGUAGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
        </>
      )}

      {/* Step 1: Business */}
      {step === 1 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.BUSINESS')}</h5>
          <p className="text-muted mb-3">{t('WAITLIST.OPTIONAL_STEP_HELP')}</p>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.BUSINESS_TYPE')}</Form.Label>
            <Form.Select name="business_type" value={formData.business_type} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {BUSINESS_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.BUSINESS_DOCUMENT')}</Form.Label>
              <Form.Control name="business_document" value={formData.business_document} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.EMPLOYEE_RANGE')}</Form.Label>
              <Form.Select name="employee_range" value={formData.employee_range} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {EMPLOYEE_RANGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.ML_MONTHLY_SALES_RANGE')}</Form.Label>
            <Form.Select name="ml_monthly_sales_range" value={formData.ml_monthly_sales_range} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {MONTHLY_SALES_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </>
      )}

      {/* Step 2: Mercado Libre Operation */}
      {step === 2 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.OPERATION')}</h5>
          <p className="text-muted mb-3">{t('WAITLIST.OPTIONAL_STEP_HELP')}</p>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.HAS_ACTIVE_ML_ACCOUNT')}</Form.Label>
              <Form.Select name="has_active_ml_account" value={formData.has_active_ml_account} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {YES_NO_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.IS_REGISTERED_BRAND')}</Form.Label>
              <Form.Select name="is_registered_brand" value={formData.is_registered_brand} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {YES_NO_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.HAS_OFFICIAL_STORE')}</Form.Label>
              <Form.Select name="has_official_store" value={formData.has_official_store} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {YES_NO_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.USES_MERCADO_ENVIOS_FULL')}</Form.Label>
              <Form.Select name="uses_mercado_envios_full" value={formData.uses_mercado_envios_full} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {YES_NO_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.PRIMARY_CATEGORY')}</Form.Label>
            <Form.Select name="primary_category" value={formData.primary_category} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {PRIMARY_CATEGORY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </>
      )}

      {/* Step 3: Commercial Fit */}
      {step === 3 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.FIT')}</h5>
          <p className="text-muted mb-3">{t('WAITLIST.OPTIONAL_STEP_HELP')}</p>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.PRIMARY_PROBLEM')}</Form.Label>
            <Form.Select name="primary_problem" value={formData.primary_problem} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {PRIMARY_PROBLEM_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.RESPONDS_MANUALLY')}</Form.Label>
              <Form.Select name="responds_manually" value={formData.responds_manually} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {YES_NO_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.USES_AUTOMATION_TOOL')}</Form.Label>
              <Form.Select name="uses_automation_tool" value={formData.uses_automation_tool} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {YES_NO_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          {formData.uses_automation_tool === 'yes' ? (
            <Form.Group className="mb-3">
              <Form.Label>{t('WAITLIST.FIELDS.AUTOMATION_TOOL_NAME')}</Form.Label>
              <Form.Control name="automation_tool_name" value={formData.automation_tool_name} onChange={handleChange} />
            </Form.Group>
          ) : null}
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.DAILY_INQUIRY_VOLUME')}</Form.Label>
              <Form.Select name="daily_inquiry_volume" value={formData.daily_inquiry_volume} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {DAILY_INQUIRY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.GOAL_INTENT')}</Form.Label>
              <Form.Select name="goal_intent" value={formData.goal_intent} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {GOAL_INTENT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.WILLINGNESS_TO_PAY')}</Form.Label>
            <Form.Select name="willingness_to_pay" value={formData.willingness_to_pay} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {WILLINGNESS_TO_PAY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.DISCOVERY_SOURCE')}</Form.Label>
            <Form.Select name="discovery_source" value={formData.discovery_source} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {DISCOVERY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          {formData.discovery_source === 'recommendation' && (
            <Form.Group className="mb-3">
              <Form.Label>{t('WAITLIST.FIELDS.REFERRAL_NAME')}</Form.Label>
              <Form.Control name="referral_name" value={formData.referral_name} onChange={handleChange} />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.SALES_UPLIFT_EXPECTATION')}</Form.Label>
            <Form.Select name="sales_uplift_expectation" value={formData.sales_uplift_expectation} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {SALES_UPLIFT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.SAMPLE_CUSTOMER_QUESTION')}</Form.Label>
            <Form.Control as="textarea" rows={3} name="sample_customer_question" value={formData.sample_customer_question} onChange={handleChange} />
          </Form.Group>
        </>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.CONFIRM')}</h5>
          <Card className="bg-light mb-3">
            <Card.Body>
              <Row>
                <Col sm={6}>
                  <p className="mb-1"><strong>{t('WAITLIST.REVIEW.NAME')}:</strong> {formData.first_name} {formData.last_name}</p>
                  <p className="mb-1"><strong>{t('WAITLIST.REVIEW.EMAIL')}:</strong> {formData.email}</p>
                  {formData.phone && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.WHATSAPP')}:</strong> {formData.phone}</p>}
                  {formData.country && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.COUNTRY')}:</strong> {formData.country}</p>}
                  {formData.city && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.CITY')}:</strong> {formData.city}</p>}
                  {formData.preferred_language && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.LANGUAGE')}:</strong> {getOptionLabel(PREFERRED_LANGUAGE_OPTIONS, formData.preferred_language, t)}</p>}
                </Col>
                <Col sm={6}>
                  {formData.business_type && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.BUSINESS_TYPE')}:</strong> {getOptionLabel(BUSINESS_TYPE_OPTIONS, formData.business_type, t)}</p>}
                  {formData.ml_monthly_sales_range && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.SALES_RANGE')}:</strong> {getOptionLabel(MONTHLY_SALES_OPTIONS, formData.ml_monthly_sales_range, t)}</p>}
                  {formData.primary_category && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.CATEGORY')}:</strong> {getOptionLabel(PRIMARY_CATEGORY_OPTIONS, formData.primary_category, t)}</p>}
                  {formData.primary_problem && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.PRIMARY_PROBLEM')}:</strong> {getOptionLabel(PRIMARY_PROBLEM_OPTIONS, formData.primary_problem, t)}</p>}
                </Col>
              </Row>
              {formData.discovery_source && (
                <p className="mb-1"><strong>{t('WAITLIST.REVIEW.DISCOVERED_VIA')}:</strong> {getDiscoveryLabel(formData.discovery_source)}</p>
              )}
              {formData.sales_uplift_expectation && (
                <p className="mb-1"><strong>{t('WAITLIST.REVIEW.SALES_UPLIFT')}:</strong> {getOptionLabel(SALES_UPLIFT_OPTIONS, formData.sales_uplift_expectation, t)}</p>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Navigation */}
      <div className="d-flex justify-content-between mt-4">
        <Button type="button" variant="secondary" onClick={handleBack} disabled={step === 0}>
          {t('WAITLIST.BACK')}
        </Button>
        {step < STEPS.length - 1 ? (
          <div className="d-flex gap-2">
            {isOptionalStep && (
              <Button variant="outline-success" type="submit" disabled={submitting || !canAdvance()}>
                {submitting ? t('WAITLIST.SUBMITTING') : t('WAITLIST.SUBMIT_NOW')}
              </Button>
            )}
            <Button type="button" variant="primary" onClick={handleNext} disabled={!canAdvance()}>
              {step === 0 ? t('WAITLIST.NEXT') : t('WAITLIST.CONTINUE_OPTIONAL')}
            </Button>
          </div>
        ) : (
          <Button variant="success" type="submit" disabled={submitting || !canAdvance()}>
            {submitting ? t('WAITLIST.SUBMITTING') : t('WAITLIST.SUBMIT_APPLICATION')}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default WaitlistForm;
